import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, Plus, RefreshCw, Loader2, ArrowDownCircle, ArrowUpCircle,
  Filter, X, Send, UserCog, Pencil, Trash2, UserPlus, Banknote, Check,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

const err = (e) => e?.response?.data?.detail || e?.message || 'Error';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtDateInput = (iso) => (iso && iso.length >= 10 ? iso.slice(0, 10) : todayISO());

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = iso.length === 10 ? new Date(iso + 'T00:00:00') : new Date(iso);
    return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${d.getFullYear()}`;
  } catch { return iso.slice(0, 10); }
};

export default function FundsPage() {
  const { admin } = useAuth();
  const [tab, setTab] = useState('balances');
  const [directors, setDirectors] = useState([]);
  const [venues, setVenues] = useState([]);
  const [balances, setBalances] = useState({ directors: [], totals: {} });
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [showInward, setShowInward] = useState(false);
  const [showOutward, setShowOutward] = useState(false);

  // ─── Loans state ────────────────────────────────────────────────────────────
  const [loans, setLoans] = useState([]);
  const [loansLoading, setLoansLoading] = useState(false);
  const [editingLoanId, setEditingLoanId] = useState(null);  // director_id being edited
  const [loanInput, setLoanInput] = useState('');
  const [savingLoan, setSavingLoan] = useState(false);

  // Ledger filters
  const [fDirector, setFDirector] = useState('all');
  const [fType, setFType] = useState('all');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fReference, setFReference] = useState('');

  // Director Balances sort
  const [balSort, setBalSort] = useState({ key: 'name', dir: 'asc' });
  const toggleBalSort = (key) => {
    setBalSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' }));
  };
  const sortedBalances = useMemo(() => {
    const list = [...(balances.directors || [])];
    const { key, dir } = balSort;
    list.sort((a, b) => {
      let av = a[key]; let bv = b[key];
      if (key === 'name') { av = (av || '').toLowerCase(); bv = (bv || '').toLowerCase(); }
      else { av = Number(av || 0); bv = Number(bv || 0); }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [balances, balSort]);

  const loadMasters = useCallback(async () => {
    try {
      const [dRes, vRes] = await Promise.all([
        axios.get(`${API}/funds/directors`),
        axios.get(`${API}/funds/venues`),
      ]);
      setDirectors(dRes.data.directors || []);
      setVenues(vRes.data.venues || []);
    } catch (e) { toast.error(err(e)); }
  }, []);

  const loadBalances = useCallback(async () => {
    setBalancesLoading(true);
    try {
      const r = await axios.get(`${API}/funds/balances`);
      setBalances(r.data || { directors: [], totals: {} });
    } catch (e) { toast.error(err(e)); }
    finally { setBalancesLoading(false); }
  }, []);

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const params = {};
      if (fDirector && fDirector !== 'all') params.director_id = fDirector;
      if (fType && fType !== 'all') params.type = fType;
      if (fDateFrom) params.date_from = fDateFrom;
      if (fDateTo) params.date_to = fDateTo;
      if (fReference) params.reference = fReference;
      const r = await axios.get(`${API}/funds/ledger`, { params });
      setLedger(r.data.transactions || []);
    } catch (e) { toast.error(err(e)); }
    finally { setLedgerLoading(false); }
  }, [fDirector, fType, fDateFrom, fDateTo, fReference]);

  useEffect(() => { loadMasters(); }, [loadMasters]);
  useEffect(() => { loadBalances(); }, [loadBalances]);
  useEffect(() => { if (tab === 'ledger') loadLedger(); }, [tab, loadLedger]);
  useEffect(() => { if (tab === 'loans') loadLoans(); }, [tab]);

  // Loans
  const loadLoans = async () => {
    setLoansLoading(true);
    try {
      const r = await axios.get(`${API}/funds/loans`);
      setLoans(r.data.loans || []);
    } catch (e) { toast.error(err(e)); }
    finally { setLoansLoading(false); }
  };

  const startEditLoan = (loan) => {
    setEditingLoanId(loan.director_id);
    setLoanInput(String(loan.amount || ''));
  };

  const cancelLoanEdit = () => { setEditingLoanId(null); setLoanInput(''); };

  const saveLoan = async (directorId) => {
    const amount = parseFloat(loanInput);
    if (isNaN(amount)) { toast.error('Enter a valid amount'); return; }
    setSavingLoan(true);
    try {
      await axios.put(`${API}/funds/loans/${directorId}`, { amount });
      toast.success('Loan updated');
      setEditingLoanId(null);
      setLoanInput('');
      await loadLoans();
    } catch (e) { toast.error(err(e)); }
    finally { setSavingLoan(false); }
  };

  // Daily broadcast settings
  const [fundsSettings, setFundsSettings] = useState({});
  const [fundsState, setFundsState] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);
  const loadSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/funds/settings`);
      setFundsSettings(r.data.settings || {});
      setFundsState(r.data.state || {});
    } catch { /* silently — endpoint may 401 during initial load */ }
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveSettings = async (patch) => {
    if (savingSettings) return;
    setSavingSettings(true);
    try {
      const r = await axios.put(`${API}/funds/settings`, patch);
      setFundsSettings(r.data.settings || {});
      toast.success('Daily-update settings saved');
    } catch (e) { toast.error(err(e)); }
    finally { setSavingSettings(false); }
  };

  const sendDailyNow = async () => {
    if (sendingDaily) return;
    if (!window.confirm('Send daily balance WhatsApp to every director above the threshold now?')) return;
    setSendingDaily(true);
    try {
      const r = await axios.post(`${API}/funds/send-daily-balance-now`);
      toast.success(`Sent ${r.data.sent}/${r.data.eligible} — ${r.data.failed} failed`);
      loadSettings();
    } catch (e) { toast.error(err(e)); }
    finally { setSendingDaily(false); }
  };

  const clearFilters = () => {
    setFDirector('all'); setFType('all');
    setFDateFrom(''); setFDateTo(''); setFReference('');
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6" data-testid="funds-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Wallet className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-600" /> MAKS Funds
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Director-to-director fund movement · append-only ledger
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none"
                    onClick={() => setShowInward(true)} data-testid="new-inward-btn">
              <ArrowDownCircle className="w-4 h-4 mr-1" />
              <span className="hidden xs:inline sm:inline">Receive Funds</span>
              <span className="inline xs:hidden sm:hidden">Receive</span>
            </Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 flex-1 sm:flex-none"
                    onClick={() => setShowOutward(true)} data-testid="new-outward-btn">
              <ArrowUpCircle className="w-4 h-4 mr-1" />
              <span className="hidden xs:inline sm:inline">Release Funds</span>
              <span className="inline xs:hidden sm:hidden">Release</span>
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => { loadBalances(); if (tab === 'ledger') loadLedger(); }}
                    data-testid="refresh-funds-btn" title="Refresh">
              <RefreshCw className="w-4 h-4" />
              <span className="ml-1 hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryCard label="Total Inward" value={inr(balances.totals?.total_in)}
                       accent="text-emerald-600" testid="kpi-total-in" />
          <SummaryCard label="Total Outward" value={inr(balances.totals?.total_out)}
                       accent="text-rose-600" testid="kpi-total-out" />
          <SummaryCard label="Net MAKS Funds" value={inr(balances.totals?.balance)}
                       accent="text-slate-900" testid="kpi-net-balance" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList data-testid="funds-tabs">
            <TabsTrigger value="balances" data-testid="tab-balances">Director Balances</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">Transaction Ledger</TabsTrigger>
            <TabsTrigger value="daily" data-testid="tab-daily">Daily Balance WhatsApp</TabsTrigger>
            <TabsTrigger value="masters" data-testid="tab-masters">Directors Master</TabsTrigger>
            <TabsTrigger value="loans" data-testid="tab-loans" className="gap-1.5">
              <Banknote className="w-3.5 h-3.5" /> Loans
            </TabsTrigger>
          </TabsList>

          {tab === 'balances' && (
            <Card className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-slate-600">
                    <SortHeader label="Name" k="name" sort={balSort} onClick={toggleBalSort} align="left" />
                    <SortHeader label="Current Balance" k="balance" sort={balSort} onClick={toggleBalSort} align="right" />
                    <SortHeader label="Total Rcvd" k="total_in" sort={balSort} onClick={toggleBalSort} align="right" />
                    <SortHeader label="Total Released" k="total_out" sort={balSort} onClick={toggleBalSort} align="right" />
                  </tr>
                </thead>
                <tbody data-testid="balances-tbody">
                  {balancesLoading ? (
                    <tr><td colSpan={4} className="px-3 sm:px-4 py-8 text-center text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : sortedBalances.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 sm:px-4 py-8 text-center text-slate-400">
                      No directors configured. Add them from the <span className="font-medium">Directors Master</span> tab.</td></tr>
                  ) : sortedBalances.map((d) => (
                    <tr key={d.director_id} className="border-b hover:bg-slate-50/60" data-testid={`balance-row-${d.director_id}`}>
                      <td className="px-3 sm:px-4 py-3 font-medium text-slate-800">{d.name}</td>
                      <td className="px-3 sm:px-4 py-3 text-right font-semibold">{inr(d.balance)}</td>
                      <td className="px-3 sm:px-4 py-3 text-right text-emerald-600">{inr(d.total_in)}</td>
                      <td className="px-3 sm:px-4 py-3 text-right text-rose-600">{inr(d.total_out)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {tab === 'ledger' && (
            <>
              <Card className="p-3 flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Director</Label>
                  <Select value={fDirector} onValueChange={setFDirector}>
                    <SelectTrigger className="w-[190px]" data-testid="ledger-filter-director">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Directors</SelectItem>
                      {directors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Type</Label>
                  <Select value={fType} onValueChange={setFType}>
                    <SelectTrigger className="w-[130px]" data-testid="ledger-filter-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="in">Inward</SelectItem>
                      <SelectItem value="out">Outward</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">From</Label>
                  <Input type="date" value={fDateFrom} onChange={(e) => setFDateFrom(e.target.value)}
                         className="w-[150px]" data-testid="ledger-filter-date-from" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">To</Label>
                  <Input type="date" value={fDateTo} onChange={(e) => setFDateTo(e.target.value)}
                         className="w-[150px]" data-testid="ledger-filter-date-to" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Reference contains</Label>
                  <Input value={fReference} onChange={(e) => setFReference(e.target.value)}
                         placeholder="e.g. cheque no" className="w-[180px]"
                         data-testid="ledger-filter-reference" />
                </div>
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" onClick={clearFilters} data-testid="ledger-clear-filters">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                  <Button onClick={loadLedger} data-testid="ledger-apply-filters">
                    <Filter className="w-4 h-4 mr-1" /> Apply
                  </Button>
                </div>
              </Card>

              <Card className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                  <thead className="bg-slate-50 border-b">
                    <tr className="text-left text-slate-600">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">From</th>
                      <th className="px-4 py-3">To</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3">Reference</th>
                      <th className="px-4 py-3 text-right">Balance</th>
                      <th className="px-4 py-3">Entered By</th>
                    </tr>
                  </thead>
                  <tbody data-testid="ledger-tbody">
                    {ledgerLoading ? (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                    ) : ledger.length === 0 ? (
                      <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No transactions found</td></tr>
                    ) : ledger.map((r) => (
                      <tr key={r.id} className="border-b hover:bg-slate-50/60" data-testid={`ledger-row-${r.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.date)}</td>
                        <td className="px-4 py-3">
                          {r.type === 'in' ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                              <ArrowDownCircle className="w-4 h-4" /> Inward
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-700 font-medium">
                              <ArrowUpCircle className="w-4 h-4" /> Outward
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {r.from_director || '-'}
                          {r.from_venue && <div className="text-xs text-slate-400">{r.from_venue}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{r.to_director || (r.type === 'out' ? 'External' : '-')}</td>
                        <td className={`px-4 py-3 text-right font-medium ${r.type === 'in' ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {r.type === 'in' ? '+' : '−'} {inr(r.amount)}
                        </td>
                        <td className="px-4 py-3 text-slate-500 max-w-[220px] truncate" title={r.reference}>{r.reference || '-'}</td>
                        <td className="px-4 py-3 text-right text-slate-800">
                          {r.running_balance != null ? inr(r.running_balance) : '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{r.entered_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}
          {tab === 'daily' && (
            <Card className="p-3 sm:p-4" data-testid="daily-broadcast-card">
              <div className="flex flex-col md:flex-row md:items-end gap-3">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Daily Balance WhatsApp</div>
                  <div className="text-sm text-slate-700 mt-1">
                    Every{' '}
                    <span className="font-semibold">
                      {(fundsSettings.frequency_days ?? 1) === 1 ? 'day' : `${fundsSettings.frequency_days} days`}
                    </span>{' '}
                    at{' '}
                    <span className="font-semibold">{fundsSettings.daily_update_time_hhmm || '09:00'} IST</span>,
                    directors with balance above{' '}
                    <span className="font-semibold">{inr(fundsSettings.min_balance_threshold ?? 100)}</span>{' '}
                    get a WhatsApp with their current MAKS Funds balance.
                    {fundsState?.last_run_at && (
                      <span className="text-xs text-slate-500 block mt-1">
                        Last run: {new Date(fundsState.last_run_at).toLocaleString('en-IN')}
                        {fundsState.last_summary && (
                          <> · sent {fundsState.last_summary.sent}/{fundsState.last_summary.eligible}</>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Time (24h)</Label>
                    <Input type="time" defaultValue={fundsSettings.daily_update_time_hhmm || '09:00'}
                           onBlur={(e) => {
                             if (e.target.value && e.target.value !== fundsSettings.daily_update_time_hhmm) {
                               saveSettings({ daily_update_time_hhmm: e.target.value });
                             }
                           }}
                           className="w-[120px]" data-testid="daily-time-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Every N days</Label>
                    <Input type="number" min="1" max="30" step="1"
                           defaultValue={fundsSettings.frequency_days ?? 1}
                           onBlur={(e) => {
                             const v = Number(e.target.value);
                             if (!Number.isNaN(v) && v >= 1 && v <= 30 && v !== Number(fundsSettings.frequency_days ?? 1)) {
                               saveSettings({ frequency_days: v });
                             }
                           }}
                           className="w-[100px]" data-testid="daily-frequency-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Min Balance (₹)</Label>
                    <Input type="number" min="0" step="1"
                           defaultValue={fundsSettings.min_balance_threshold ?? 100}
                           onBlur={(e) => {
                             const v = Number(e.target.value);
                             if (!Number.isNaN(v) && v !== Number(fundsSettings.min_balance_threshold ?? 100)) {
                               saveSettings({ min_balance_threshold: v });
                             }
                           }}
                           className="w-[110px]" data-testid="daily-threshold-input" />
                  </div>
                  <Button variant={fundsSettings.daily_update_enabled === false ? 'outline' : 'default'}
                          size="sm"
                          onClick={() => saveSettings({ daily_update_enabled: !fundsSettings.daily_update_enabled })}
                          disabled={savingSettings}
                          data-testid="daily-toggle-btn">
                    {fundsSettings.daily_update_enabled === false ? 'Enable' : 'Disable'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={sendDailyNow} disabled={sendingDaily}
                          data-testid="send-daily-now-btn">
                    {sendingDaily ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                    Send Now
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {tab === 'masters' && (
            <MastersPanel directors={directors} onChanged={loadMasters} />
          )}

          {tab === 'loans' && (
            <LoansPanel
              loans={loans}
              loading={loansLoading}
              onRefresh={loadLoans}
              editingLoanId={editingLoanId}
              loanInput={loanInput}
              setLoanInput={setLoanInput}
              savingLoan={savingLoan}
              onEdit={startEditLoan}
              onCancel={cancelLoanEdit}
              onSave={saveLoan}
            />
          )}
        </Tabs>

        <InwardDialog open={showInward} onClose={() => setShowInward(false)}
                      directors={directors} venues={venues}
                      onSaved={() => { loadBalances(); if (tab === 'ledger') loadLedger(); }} />

        <OutwardDialog open={showOutward} onClose={() => setShowOutward(false)}
                       directors={balances.directors || []}
                       onSaved={() => { loadBalances(); if (tab === 'ledger') loadLedger(); }} />
      </div>
    </DashboardLayout>
  );
}


// ─── Loans Panel ─────────────────────────────────────────────────────────────

function LoansPanel({ loans, loading, onRefresh, editingLoanId, loanInput, setLoanInput, savingLoan, onEdit, onCancel, onSave }) {
  const inrFull = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return iso; }
  };
  const totalLoan = loans.reduce((s, l) => s + (l.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="loans-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-slate-800 flex items-center gap-2">
            <Banknote className="w-4 h-4 text-indigo-600" /> Director Loans
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Track outstanding loans per director. Completely separate from fund balances.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-xs text-slate-500">Total Outstanding</div>
            <div className="font-bold text-indigo-700 font-mono">{inrFull(totalLoan)}</div>
          </div>
          <button onClick={onRefresh} disabled={loading}
            className="h-8 w-8 flex items-center justify-center border rounded-md hover:bg-slate-100 transition-colors"
            data-testid="loans-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin inline" />
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="loans-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Director Name</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600 min-w-[160px]">Loan Amount</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 min-w-[180px]">Last Updated</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600 min-w-[160px]">Updated By</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {loans.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                    No directors found. Add directors in the Directors Master tab.
                  </td>
                </tr>
              ) : loans.map((loan) => {
                const isEditing = editingLoanId === loan.director_id;
                return (
                  <tr key={loan.director_id} className="border-b hover:bg-slate-50 transition-colors"
                    data-testid={`loan-row-${loan.director_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{loan.director_name}</div>
                      {loan.director_email && (
                        <div className="text-xs text-slate-400 font-mono">{loan.director_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          value={loanInput}
                          onChange={e => setLoanInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') onSave(loan.director_id); if (e.key === 'Escape') onCancel(); }}
                          autoFocus
                          placeholder="0"
                          className="w-36 text-right border border-indigo-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 font-mono"
                          data-testid={`loan-input-${loan.director_id}`}
                        />
                      ) : (
                        <span className={`font-semibold font-mono ${loan.amount > 0 ? 'text-indigo-700' : 'text-slate-400'}`}
                          data-testid={`loan-amount-${loan.director_id}`}>
                          {loan.amount > 0 ? inrFull(loan.amount) : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      <span data-testid={`loan-date-${loan.director_id}`}>{fmtDate(loan.updated_at)}</span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className={`font-mono ${loan.updated_by ? 'text-slate-600' : 'text-slate-300'}`}
                        data-testid={`loan-by-${loan.director_id}`}>
                        {loan.updated_by || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => onSave(loan.director_id)}
                            disabled={savingLoan}
                            className="h-7 px-3 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-xs flex items-center gap-1 disabled:opacity-60 transition-colors"
                            data-testid={`loan-save-${loan.director_id}`}>
                            {savingLoan ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Save
                          </button>
                          <button onClick={onCancel}
                            className="h-7 px-2 rounded border hover:bg-slate-100 text-xs text-slate-600 transition-colors"
                            data-testid={`loan-cancel-${loan.director_id}`}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => onEdit(loan)}
                          className="h-7 px-3 rounded border hover:bg-indigo-50 hover:border-indigo-300 text-xs text-slate-600 flex items-center gap-1 ml-auto transition-colors"
                          data-testid={`loan-edit-${loan.director_id}`}>
                          <Pencil className="w-3 h-3" />
                          {loan.amount > 0 ? 'Update' : 'Add'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {loans.length > 0 && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td className="px-4 py-3 font-bold text-slate-700">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-indigo-700 font-mono" data-testid="loans-total">
                    {inrFull(totalLoan)}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </Card>

      {/* Info note */}
      <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
        <span className="inline-block w-2 h-2 rounded-full bg-indigo-300" />
        Loan records are independent of fund balances and transactions. Changes here do not affect director balances.
      </p>
    </div>
  );
}


function SummaryCard({ label, value, accent, testid }) {
  return (
    <Card className="p-3 sm:p-4" data-testid={testid}>
      <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg sm:text-2xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</div>
    </Card>
  );
}


function SortHeader({ label, k, sort, onClick, align = 'left' }) {
  const active = sort.key === k;
  const dirArrow = active ? (sort.dir === 'asc' ? '▲' : '▼') : '';
  return (
    <th className={`px-3 sm:px-4 py-3 select-none cursor-pointer ${align === 'right' ? 'text-right' : 'text-left'}`}
        onClick={() => onClick(k)}
        data-testid={`sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${active ? 'text-slate-900' : 'text-slate-600'}`}>
        {label} <span className="text-[10px]">{dirArrow || '↕'}</span>
      </span>
    </th>
  );
}


function InwardDialog({ open, onClose, directors, venues, onSaved }) {
  const [fromVenue, setFromVenue] = useState('');
  const [fromDirectorId, setFromDirectorId] = useState('');
  const [toDirectorId, setToDirectorId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFromVenue(''); setFromDirectorId(''); setToDirectorId('');
      setAmount(''); setDate(todayISO()); setReference('');
    }
  }, [open]);

  const submit = async () => {
    if (!toDirectorId) return toast.error('Select the receiving Director');
    if (!amount || Number(amount) <= 0) return toast.error('Enter a valid amount');
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        to_director_id: toDirectorId,
        from_director_id: fromDirectorId || null,
        from_venue: fromVenue,
        amount: Number(amount), date, reference,
      };
      const r = await axios.post(`${API}/funds/inward`, payload);
      const notif = r.data.notification || {};
      toast.success(notif.success ? 'Recorded · WhatsApp sent' : 'Recorded (WA send skipped)');
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inward-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownCircle className="w-5 h-5 text-emerald-600" /> Receive MAKS Funds
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>From Venue</Label>
            <Select value={fromVenue} onValueChange={setFromVenue}>
              <SelectTrigger data-testid="inward-venue"><SelectValue placeholder="Select venue (optional)" /></SelectTrigger>
              <SelectContent>
                {venues.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From Director</Label>
            <Select value={fromDirectorId} onValueChange={setFromDirectorId}>
              <SelectTrigger data-testid="inward-from-director"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {directors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>To Director *</Label>
            <Select value={toDirectorId} onValueChange={setToDirectorId}>
              <SelectTrigger data-testid="inward-to-director"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {directors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount *</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                   min="0" step="1" data-testid="inward-amount" />
          </div>
          <div>
            <Label>Date *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="inward-date" />
          </div>
          <div className="col-span-2">
            <Label>Reference / Notes</Label>
            <Textarea rows={2} value={reference} onChange={(e) => setReference(e.target.value)}
                      data-testid="inward-reference" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="inward-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
                    : <><Send className="w-4 h-4 mr-1" /> Record Inward</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OutwardDialog({ open, onClose, directors, onSaved }) {
  const [fromDirectorId, setFromDirectorId] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayISO());
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFromDirectorId(''); setAmount(''); setDate(todayISO()); setReference('');
    }
  }, [open]);

  const selected = directors.find((d) => d.director_id === fromDirectorId);

  const submit = async () => {
    if (!fromDirectorId) return toast.error('Select the paying Director');
    if (!amount || Number(amount) <= 0) return toast.error('Enter a valid amount');
    if (selected && Number(amount) > Number(selected.balance || 0) + 1e-6) {
      return toast.error(`Insufficient balance. ${selected.name} has ${inr(selected.balance)}.`);
    }
    if (saving) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/funds/outward`, {
        from_director_id: fromDirectorId,
        amount: Number(amount), date, reference,
      });
      const notif = r.data.notification || {};
      toast.success(notif.success ? 'Released · WhatsApp sent' : 'Released (WA send skipped)');
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="outward-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-rose-600" /> Release MAKS Funds
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>From Director *</Label>
            <Select value={fromDirectorId} onValueChange={setFromDirectorId}>
              <SelectTrigger data-testid="outward-from-director"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {directors.map((d) => (
                  <SelectItem key={d.director_id} value={d.director_id}>
                    {d.name} — Available {inr(d.balance)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <div className="text-xs text-slate-500 mt-1">
                Available: <span className="font-semibold text-slate-800">{inr(selected.balance)}</span>
              </div>
            )}
          </div>
          <div>
            <Label>Amount *</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                   min="0" step="1" data-testid="outward-amount" />
          </div>
          <div>
            <Label>Date *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   data-testid="outward-date" />
          </div>
          <div className="col-span-2">
            <Label>Reference / Notes</Label>
            <Textarea rows={2} value={reference} onChange={(e) => setReference(e.target.value)}
                      data-testid="outward-reference" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700"
                  data-testid="outward-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
                    : <><Send className="w-4 h-4 mr-1" /> Record Outward</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MastersPanel({ directors, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState('');

  const openAdd = () => { setEditing(null); setShowForm(true); };
  const openEdit = (d) => { setEditing(d); setShowForm(true); };

  const remove = async (d) => {
    if (busyId) return;
    if (!window.confirm(`Delete director "${d.name}"?\nIf they have ledger entries they'll be archived instead.`)) return;
    setBusyId(d.id);
    try {
      const r = await axios.delete(`${API}/funds/directors/${d.id}`);
      toast.success(r.data.archived
        ? 'Director archived (had ledger history)'
        : 'Director deleted');
      onChanged && onChanged();
    } catch (e) { toast.error(err(e)); }
    finally { setBusyId(''); }
  };

  return (
    <>
      <Card className="p-3 flex items-center justify-between">
        <div className="text-sm text-slate-500">
          Directors here appear in every <span className="font-medium">Receive</span> /
          <span className="font-medium"> Release</span> dropdown and in Expense approvals.
        </div>
        <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800"
                data-testid="add-director-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Add Director
        </Button>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-600">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Mobile</th>
              <th className="px-4 py-3 text-right">Balance</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="masters-tbody">
            {directors.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                No directors yet. Click <span className="font-medium">Add Director</span> to create the first.
              </td></tr>
            ) : directors.map((d) => (
              <tr key={d.id} className="border-b hover:bg-slate-50/60" data-testid={`director-row-${d.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
                  <UserCog className="w-4 h-4 text-slate-400" /> {d.name}
                </td>
                <td className="px-4 py-3 text-slate-600">{d.email || '-'}</td>
                <td className="px-4 py-3 text-slate-600">{d.mobile || '-'}</td>
                <td className="px-4 py-3 text-right font-semibold">{inr(d.balance)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => openEdit(d)}
                          data-testid={`edit-director-${d.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(d)} disabled={busyId === d.id}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-1"
                          data-testid={`delete-director-${d.id}`}>
                    {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                     : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <DirectorFormDialog open={showForm} onClose={() => setShowForm(false)}
                          initial={editing}
                          onSaved={() => { setShowForm(false); onChanged && onChanged(); }} />
    </>
  );
}


function DirectorFormDialog({ open, onClose, initial, onSaved }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setEmail(initial?.email || '');
      setMobile(initial?.mobile || '');
    }
  }, [open, initial]);

  const submit = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const digitsMobile = mobile.replace(/\D/g, '');
    if (!trimmedName) return toast.error('Name is required');
    if (!trimmedEmail) return toast.error('Email is required');
    if (digitsMobile.length < 10) return toast.error('Mobile must be at least 10 digits');
    if (saving) return;
    setSaving(true);
    try {
      const payload = { name: trimmedName, email: trimmedEmail, mobile: digitsMobile };
      if (isEdit) {
        await axios.put(`${API}/funds/directors/${initial.id}`, payload);
        toast.success('Director updated');
      } else {
        await axios.post(`${API}/funds/directors`, payload);
        toast.success('Director added');
      }
      onSaved && onSaved();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="director-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-slate-600" />
            {isEdit ? 'Edit Director' : 'Add Director'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Alwyn D'Souza" data-testid="director-name-input" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="director@makspickleball.com" data-testid="director-email-input" />
          </div>
          <div>
            <Label>Mobile Number *</Label>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value)}
                   placeholder="9876543210 (10 digits)" data-testid="director-mobile-input" />
            <p className="text-xs text-slate-500 mt-1">
              Used to send MAKS Funds WhatsApp notifications.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800"
                  data-testid="director-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
                    : isEdit ? 'Save Changes' : 'Add Director'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

